import { installVue2Compat } from "./plugins/vue2-compat";
import { createApp } from "vue";
import { createPinia } from "pinia";
import Notifications from "@kyvg/vue3-notification";
import NProgress from "nprogress";
import "nprogress/nprogress.css";

import App from "./App.vue";
import router from "./router";
import { useAppStore } from "./stores/app";
import http, { setAuthToken } from "./api/http";
import { ovReq } from "./api/ov-req";
import { notify } from "./composables/useNotify";
import ovDataPlugin from "./plugins/ov-data";
import { createModalPlugin } from "./plugins/modal";
import { installClipboard } from "./plugins/clipboard";
import BootstrapVue from "bootstrap-vue";
import { installBootstrapTabs } from "./plugins/bootstrap-tabs";
import { installBootstrapTooltipCompat } from "./plugins/bootstrap-tooltip";
import { vue3Debounce } from "vue-debounce";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-vue/dist/bootstrap-vue.css";
import "./styles/style.scss";
import "./styles/dashboard-ds.scss";

import { CkeditorPlugin } from "@ckeditor/ckeditor5-vue";
import Editor from "./components/Editor.vue";

import FileList from "./components/form/FileList";
import DocList from "./components/form/DocList";
import TimeframeRestriction from "./components/form/TimeframeRestriction";
import GeoTargeting from "./components/form/GeoTargeting";
import OsTargetingVersionBlacklist from "./components/form/OsTargetingVersionBlacklist";
import OsTargetingVersionBlacklistObject from "./components/form/OsTargetingVersionBlacklistObject";
import DeviceTypeTargeting from "./components/form/DeviceTypeTargeting";
import OsNetworkTargeting from "./components/form/OsNetworkTargeting";
import FrequencyCap from "./components/form/FrequencyCap";
import NewFeature from "./components/form/NewFeature";
import PasswordInput from "./components/form/PasswordInput";
import ValidatedInput from "./components/form/ValidatedInput";
import PlacementDefaults from "./components/PlacementDefaults";
import OfferDefaults from "./components/OfferDefaults";
import ComplexOfferDefaults from "./components/ComplexOfferDefaults";
import OfferPausedReason from "./components/OfferPausedReason";
import OfferVtaIndicator from "./components/OfferVtaIndicator";
import MmpIcon from "./components/MmpIcon";
import Filters from "./components/Filters";
import ActivityLogTable from "./components/ActivityLogTable.vue";
import ActivityLogTablePipe from "./components/ActivityLogTablePipe/ActivityLogTablePipe.vue";
import Statistics from "./views/reports/PublisherStatistics.vue";
import Highlighter from "./components/TextHighlight.vue";
import SortColumn from "./components/SortColumn";
import Entity from "./components/Entity";
import Paginate from "./components/Paginate";
import OvModal from "./components/OvModal";
import QuickEdit from "./components/QuickEdit";
import QuickEditSelect from "./components/QuickEditSelect";
import URLInput from "./components/form/URLInput";
import MacroInput from "./components/form/MacroInput";
import NumberInput from "./components/form/NumberInput";
import ScheduledInput from "./components/form/ScheduledInput";
import CtrGoal from "./components/form/CtrGoal";
import Impratio from "./components/form/impratio.vue";
import ImpressionRatio from "./components/form/ImpressionRatio";
import CtrGoalDisplay from "./components/CtrGoalDisplay";
import ImpratioDisplay from "./components/ImpratioDisplay";
import Priority from "./components/form/Priority";
import ClicksVolume from "./components/form/ClicksVolume.vue";
import ClickList from "./components/form/ClickList";
import PostbacksTable from "./components/PostbacksTable";
import TableEditableCell from "./components/TableEditableCell";
import ColorPicker from "./components/form/ColorPicker";
import SecretToken from "./components/form/SecretToken";
import SlackNotifyToggle from "./components/form/SlackNotifyToggle";
import ToggleButton from "./components/ToggleButton.vue";
import Loader from "./plugins/loader";
import DateRangePicker from "vue2-daterange-picker/dist/vue2-daterange-picker.umd.min.js";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import "./plugins/date-format";
import OvDateTimePicker from "./components/form/OvDateTimePicker";
import DatePicker from "./components/form/DatePicker.vue";
import YearPicker from "./components/form/YearPicker";
import MonthPicker from "./components/form/MonthPicker.vue";
import OvVSelect from "@/components/OvVSelect.vue";
import "vue-select/dist/vue-select.css";
import OvMultiselect from "@/components/OvMultiselect.vue";
import "vue-multiselect/dist/vue-multiselect.min.css";
import SelectPublisher from "@/components/filters-selectors/SelectPublisher";
import SelectAdvertiser from "@/components/filters-selectors/SelectAdvertiser";
import SelectOffer from "@/components/filters-selectors/SelectOffer";
import SelectPlacement from "@/components/filters-selectors/SelectPlacement";
import SelectSubsource from "@/components/filters-selectors/SelectSubsource";
import SelectOsVersion from "@/components/filters-selectors/SelectOsVersion";
import SelectAM from "@/components/filters-selectors/SelectAM";
import VueApexCharts from "vue-apexcharts";
import ReportGraph from "@/components/ReportGraph";
import ReportKpi from "@/components/ReportKpi";
import ReportsWidget from "@/components/ReportsWidget";
import OvTableScroll from "@/components/OvTableScroll";
import SelectedItemsTab from "@/components/SelectedItemsTab.vue";
import Chip from "@/components/Chip";
import OvPageHeader from "@/design-system/components/OvPageHeader.vue";
import OvButton from "@/design-system/components/OvButton.vue";
import OvEmptyState from "@/design-system/components/OvEmptyState.vue";

NProgress.configure({ showSpinner: false, trickleSpeed: 200 });

const platformName = import.meta.env.VUE_APP_PLATFORM;
const poweredByOvalio = import.meta.env.VUE_APP_POWERED_BY_OVALIO === "true";

async function init() {
	const pinia = createPinia();
	const app = createApp(App);

	app.use(pinia);
	app.use(BootstrapVue);
	installBootstrapTooltipCompat(app);
	installVue2Compat(app);
	installBootstrapTabs(app);
	app.use(createModalPlugin());
	installClipboard(app);
	app.directive(
		"debounce",
		vue3Debounce({
			defaultTime: "500ms",
			listenTo: ["input"],
		}),
	);

	app.use(Notifications);
	app.use(CkeditorPlugin);
	app.use(VueApexCharts);
	app.component("editor", Editor);
	app.component("file-list", FileList);
	app.component("doc-list", DocList);
	app.component("timeframe-restriction", TimeframeRestriction);
	app.component("geo-targeting", GeoTargeting);
	app.component("os-targeting-version-blacklist", OsTargetingVersionBlacklist);
	app.component("os-targeting-version-blacklist-object", OsTargetingVersionBlacklistObject);
	app.component("device-type-targeting", DeviceTypeTargeting);
	app.component("os-network-targeting", OsNetworkTargeting);
	app.component("frequency-cap", FrequencyCap);
	app.component("new-feature", NewFeature);
	app.component("password-input", PasswordInput);
	app.component("validated-input", ValidatedInput);
	app.component("placement-defaults", PlacementDefaults);
	app.component("offer-defaults", OfferDefaults);
	app.component("complex-offer-defaults", ComplexOfferDefaults);
	app.component("offer-paused-reason", OfferPausedReason);
	app.component("offer-vta-indicator", OfferVtaIndicator);
	app.component("mmp-icon", MmpIcon);
	app.component("filters", Filters);
	app.component("activity-log-table", ActivityLogTable);
	app.component("activity-log-table-pipe", ActivityLogTablePipe);
	app.component("publisher-statistics", Statistics);
	app.component("text-highlight", Highlighter);
	app.component("ov-th", SortColumn);
	app.component("entity", Entity);
	app.component("paginate", Paginate);
	app.component("ov-modal", OvModal);
	app.component("quick-edit", QuickEdit);
	app.component("quick-edit-select", QuickEditSelect);
	app.component("url-input", URLInput);
	app.component("macro-input", MacroInput);
	app.component("number-input", NumberInput);
	app.component("scheduled-input", ScheduledInput);
	app.component("ctr-goal", CtrGoal);
	app.component("impratio", Impratio);
	app.component("impression-ratio", ImpressionRatio);
	app.component("ctr-goal-display", CtrGoalDisplay);
	app.component("impratio-display", ImpratioDisplay);
	app.component("priority", Priority);
	app.component("clicks-volume", ClicksVolume);
	app.component("click-list", ClickList);
	app.component("postbacks-table", PostbacksTable);
	app.component("table-editable-cell", TableEditableCell);
	app.component("color-picker", ColorPicker);
	app.component("secret-token", SecretToken);
	app.component("slack-notify-toggle", SlackNotifyToggle);
	app.component("ToggleButton", ToggleButton);
	app.component("toggle-button", ToggleButton);
	app.component("loading", Loader);
	app.component("date-range-picker", DateRangePicker);
	app.component("ov-date-time-picker", OvDateTimePicker);
	app.component("date-picker", DatePicker);
	app.component("year-picker", YearPicker);
	app.component("month-picker", MonthPicker);
	app.component("v-select", OvVSelect);
	app.component("multiselect", OvMultiselect);
	app.component("select-publisher", SelectPublisher);
	app.component("select-advertiser", SelectAdvertiser);
	app.component("select-offer", SelectOffer);
	app.component("select-placement", SelectPlacement);
	app.component("select-subsource", SelectSubsource);
	app.component("select-os-version", SelectOsVersion);
	app.component("select-am", SelectAM);
	app.component("apexchart", VueApexCharts);
	app.component("report-graph", ReportGraph);
	app.component("report-kpi", ReportKpi);
	app.component("reports-widget", ReportsWidget);
	app.component("ov-table-scroll", OvTableScroll);
	app.component("selected-items-tab", SelectedItemsTab);
	app.component("chip", Chip);
	app.component("OvPageHeader", OvPageHeader);
	app.component("OvButton", OvButton);
	app.component("OvEmptyState", OvEmptyState);

	app.directive("rightclick", {
		beforeMount(el, binding) {
			el.addEventListener("contextmenu", (event) => {
				event.preventDefault();
				binding.value(event);
			});
		},
	});

	app.config.globalProperties.$ovReq = ovReq;
	app.config.globalProperties.$ovNotify = notify;
	app.config.globalProperties.$platformName = platformName;
	app.config.globalProperties.$poweredByOvalio = poweredByOvalio;

	app.use(ovDataPlugin, { ovReq });

	const appStore = useAppStore(pinia);
	app.config.globalProperties.$store = {
		state: appStore,
		dispatch(action, payload) {
			if (action === "setUser") return appStore.setUser(payload);
			if (action === "closeSidebar") return appStore.closeSidebar();
			throw new Error("Unsupported store action: " + action);
		},
		commit(mutation, payload) {
			if (mutation === "toggle") return appStore.toggle(payload);
			throw new Error("Unsupported store mutation: " + mutation);
		},
	};
	let user = localStorage.getItem("user");
	let token = localStorage.getItem("authToken");
	if (user) {
		try {
			user = JSON.parse(user);
		} catch (e) {
			user = null;
		}
	}

	if (token) {
		try {
			setAuthToken(token);
			const resp = await http.get("user/getCurrent");
			if (resp.data.err === 0) {
				let settings = {};
				try {
					const settingsResp = await http.get("settings/getConfig");
					settings = settingsResp.data?.data?.settings || {};
				} catch (e) {
					// settings are optional on session restore
				}
				appStore.setUser({
					...resp.data.data,
					settings,
					token,
				});
			} else {
				token = null;
			}
		} catch (e) {
			token = null;
			appStore.setUser(null);
		}
	}

	router.beforeEach((to, from, next) => {
		appStore.closeSidebar();
		NProgress.start();

		if (to.name === "login") {
			if (appStore.user) {
				next({ name: "dashboard" });
				return;
			}
		} else if (!appStore.user) {
			if (to.name !== "forgot-password" && to.name !== "reset-password") {
				next({ name: "login" });
				return;
			}
		}

		next();
	});

	router.afterEach(() => {
		NProgress.done();
	});

	app.use(router);

	app.mount("#app");
}

init();
